import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  ArrowLeft, 
  Plus, 
  MessageSquare, 
  ThumbsUp, 
  ThumbsDown,
  Clock,
  User,
  Crown,
  X,
  Sparkles,
  Trash2
} from "lucide-react";
import { toast } from "sonner";

export default function CategoryPostsPage() {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const { user } = useAuth();
  const [category, setCategory] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState("");
  const [newPostContent, setNewPostContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, [categoryId, page]);

  const fetchPosts = async () => {
    try {
      const [categoriesRes, postsRes] = await Promise.all([
        axios.get(`${API}/forum/categories`),
        axios.get(`${API}/forum/categories/${categoryId}/posts?page=${page}`)
      ]);
      
      const cat = categoriesRes.data.find(c => c.id === categoryId);
      setCategory(cat);
      setPosts(postsRes.data.posts);
      setTotalPages(postsRes.data.pages);
    } catch (error) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostTitle.trim() || !newPostContent.trim()) {
      toast.error("Please fill in both title and content");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/forum/posts`, {
        category_id: categoryId,
        title: newPostTitle,
        content: newPostContent
      });
      
      setPosts([response.data, ...posts]);
      setShowNewPost(false);
      setNewPostTitle("");
      setNewPostContent("");
      toast.success("Post created!");
    } catch (error) {
      toast.error("Failed to create post");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (postId, vote) => {
    try {
      const response = await axios.post(`${API}/forum/posts/${postId}/vote?vote=${vote}`, {});
      setPosts(posts.map(p => 
        p.post_id === postId ? { ...p, vote_count: response.data.vote_count } : p
      ));
    } catch (error) {
      toast.error("Failed to vote");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await axios.delete(`${API}/forum/posts/${postId}`);
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold animate-pulse font-serif text-xl">Loading posts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="category-posts-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/community")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Community
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl font-bold">{category?.name || categoryId}</h1>
              <p className="text-muted-foreground text-sm">{category?.description}</p>
            </div>
            <Button 
              onClick={() => setShowNewPost(true)}
              className="bg-gold text-primary-foreground"
              data-testid="new-post-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>

        {/* New Post Form */}
        {showNewPost && (
          <Card className="mb-6 border-gold/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Create New Post</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowNewPost(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Post title"
                value={newPostTitle}
                onChange={(e) => setNewPostTitle(e.target.value)}
                data-testid="post-title-input"
              />
              <Textarea
                placeholder="What's on your mind?"
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                rows={4}
                data-testid="post-content-input"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewPost(false)}>Cancel</Button>
                <Button 
                  onClick={handleCreatePost} 
                  disabled={submitting}
                  className="bg-gold text-primary-foreground"
                  data-testid="submit-post-btn"
                >
                  {submitting ? "Posting..." : "Post"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Posts List */}
        <div className="space-y-4">
          {posts.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No posts yet. Be the first to start a discussion!</p>
              </CardContent>
            </Card>
          ) : (
            posts.map(post => (
              <Card 
                key={post.post_id} 
                className="hover:border-gold/30 transition-colors cursor-pointer"
                onClick={() => navigate(`/community/post/${post.post_id}`)}
                data-testid={`post-${post.post_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Vote buttons */}
                    <div className="flex flex-col items-center gap-1" onClick={e => e.stopPropagation()}>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => handleVote(post.post_id, 1)}
                      >
                        <ThumbsUp className="h-4 w-4" />
                      </Button>
                      <span className={`text-sm font-medium ${post.vote_count > 0 ? 'text-green-500' : post.vote_count < 0 ? 'text-red-500' : ''}`}>
                        {post.vote_count}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => handleVote(post.post_id, -1)}
                      >
                        <ThumbsDown className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Post content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-lg mb-1 line-clamp-2">{post.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{post.content}</p>
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          {post.author?.is_founding_member ? (
                            <Sparkles className="h-3 w-3 text-amber-500" />
                          ) : post.author?.is_premium ? (
                            <Crown className="h-3 w-3 text-gold" />
                          ) : (
                            <User className="h-3 w-3" />
                          )}
                          <span className={post.author?.is_founding_member || post.author?.is_founder ? "text-amber-500 font-medium" : ""}>
                            {post.author?.name || "Anonymous"}
                          </span>
                          {post.author?.username && (
                            <span className="text-[10px] text-muted-foreground">@{post.author.username}</span>
                          )}
                          {post.author?.is_founder && (
                            <Badge className="bg-primary text-primary-foreground border-0 text-[10px] px-1.5 py-0 ml-1">
                              Founder
                            </Badge>
                          )}
                          {post.author?.is_founding_member && (
                            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black border-0 text-[10px] px-1.5 py-0 ml-1">
                              Founding
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>{formatTimeAgo(post.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          <span>{post.reply_count} replies</span>
                        </div>
                        {user?.is_site_admin && (
                          <div className="ml-auto" onClick={e => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`delete-post-${post.post_id}`}
                              onClick={() => handleDeletePost(post.post_id)}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              <span className="text-xs">Delete</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
